import { useAppDispatch } from '@store';
import { deleteFolder, updateFolder } from '@store/slices/foldersSlice';
import { notifications } from '@mantine/notifications';
import { useIntl } from 'react-intl';
import { Folder } from '../types';

export const useFolderActionsRedux = (
  folder: Folder,
  callbacks?: { onMutationComplete?: () => void }
) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();

  const handleArchive = async () => {
    try {
      await dispatch(
        updateFolder({
          folderId: folder.id,
          updates: { isArchived: !folder.isArchived },
        })
      ).unwrap();

      callbacks?.onMutationComplete?.();

      notifications.show({
        color: 'green',
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage(
          { id: folder.isArchived ? 'folderUnarchived' : 'folderArchived' },
          { name: folder.folderName }
        ),
      });
    } catch (error: any) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: error.message,
      });
    }
  };

  const handleStatusChange = async (status: string) => {
    try {
      await dispatch(
        updateFolder({
          folderId: folder.id,
          updates: { backOfficeStatus: parseInt(status, 10) },
        })
      ).unwrap();

      callbacks?.onMutationComplete?.();

      notifications.show({
        color: 'green',
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage(
          { id: 'backOfficeStatusUpdated' },
          { name: folder.folderName }
        ),
      });
    } catch (error: any) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: error.message,
      });
    }
  };

  const handleDelete = async () => {
    try {
      await dispatch(
        deleteFolder({
          folderId: folder.id,
        })
      ).unwrap();

      callbacks?.onMutationComplete?.();

      notifications.show({
        color: 'green',
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage(
          { id: 'folderDeleted' },
          { name: folder.folderName }
        ),
      });
    } catch (error: any) {
      notifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: error.message,
      });
    }
  };

  return {
    handleArchive,
    handleStatusChange,
    handleDelete,
  };
};
